import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(undefined);

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [currentPath, setCurrentPath] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (token) => {
    try {
      const response = await fetch(`${API}/profile/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        return data;
      }
      return null;
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching profile:', error);
      return null;
    }
  }, []);

  const fetchCurrentPath = useCallback(async (token) => {
    try {
      const response = await fetch(`${API}/user-paths/me/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentPath(data);
        return data;
      }
      return null;
    } catch (error) {
      // No current path is OK for new users
      return null;
    }
  }, []);

  const createProfile = useCallback(async (userId, email, token) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${API}/profile`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ user_id: userId, email, signup_ref: sessionStorage.getItem('signup_ref') || '' })
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        return data;
      }
      return null;
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error creating profile:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    const initializeSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          const token = session.access_token;
          let profileData = await fetchProfile(token);
          if (!profileData) {
            profileData = await createProfile(session.user.id, session.user.email, token);
          }
          if (profileData?.is_onboarded) {
            await fetchCurrentPath(token);
          }
        }
      } catch (error) {
        process.env.NODE_ENV === "development" && console.error('Error initializing session:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          const token = session.access_token;
          let profileData = await fetchProfile(token);
          if (!profileData) {
            profileData = await createProfile(session.user.id, session.user.email, token);
          }
          if (profileData?.is_onboarded) {
            await fetchCurrentPath(token);
          }
        } else {
          setProfile(null);
          setCurrentPath(null);
        }
        
        setLoading(false);
      }
    );

    return () => subscription?.unsubscribe();
  }, [fetchProfile, createProfile, fetchCurrentPath]);

  const signInWithMagicLink = useCallback(async (email) => {
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectUrl,
          shouldCreateUser: true
        }
      });
      if (error) {
        return {
          error: {
            message: error.message || 'Failed to send magic link.',
            status: error.status,
            code: error.code || error.name,
          }
        };
      }
      return { error: null };
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Magic link error:', error);
      const msg = String(error?.message || error || '');
      if (msg.includes('body stream already read') || msg.includes('unexpected_failure')) {
        return { error: { message: 'Unable to send magic link to this email. Please try a different email address or contact support.' } };
      }
      return { error: { message: 'Something went wrong. Please try again.' } };
    }
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setProfile(null);
  }, []);

  const getAuthHeaders = useCallback(() => {
    if (session?.access_token) {
      return { 'Authorization': `Bearer ${session.access_token}` };
    }
    return {};
  }, [session]);

  const refreshProfile = useCallback(async () => {
    if (session) {
      await fetchProfile(session.access_token);
      await fetchCurrentPath(session.access_token);
    }
  }, [session, fetchProfile, fetchCurrentPath]);

  const updateProfile = useCallback(async (updates) => {
    if (!session) return null;
    try {
      const response = await fetch(`${API}/profile/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        return data;
      }
      return null;
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error updating profile:', error);
      return null;
    }
  }, [session]);

  const isOnboarded = profile?.is_onboarded ?? false;
  const isAdmin = profile?.role === 'admin';
  const hasPath = !!currentPath;

  return (
    <AuthContext.Provider value={{
      session,
      user,
      profile,
      currentPath,
      loading,
      isOnboarded,
      isAdmin,
      hasPath,
      signInWithMagicLink,
      signOut,
      refreshProfile,
      updateProfile,
      getAuthHeaders
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
